import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import { LoadingIcon } from '@/components/icons';
import { MANAGEMENT_PAGE_SIZE as pageSize } from '@/constants';
import useAuth from '@/data/hook/useAuth';
import useCourseEditing from '@/data/hook/useCourseEditing';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { updateActivityFromLesson } from '@/data/services/_deprecated/activityServices';
import { updateLesson } from '@/data/services/lessonServices';
import { lessonResourceQuerykeys } from '@/data/services/querykeys';
import {
  createLessonResource,
  createResource,
  getResource,
  removeLessonResource,
  ResourceFields,
  ResourceParams,
  updateLessonResource,
} from '@/data/services/_deprecated/resourceServices';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import { isPulished } from '@/functions/handleCourseStatusIcon';
import CoursePath from '@/models/CoursePath';
import Lesson from '@/models/Lesson';
import Resource from '@/models/_deprecated/Resource';
import ScheduledLesson from '@/models/ScheduledLesson';
import { ROLES } from '@/utils/roles';
import DynamicResourceForm from '@/components/resource/form/DynamicResourceForm';

import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import LessonResourceEditor from '../form/LessonResourceEditor';

type PickLesson = Pick<Lesson, 'id' | 'status'>;
type PickScheduledLesson = Pick<ScheduledLesson, 'id' | 'type' | 'klass'>;

export const mutateCreate = ({ body }: Omit<ResourceParams, 'resourceId'>) =>
  createResource(body);

type Props = {
  lesson: PickLesson;
  scheduledLesson?: PickScheduledLesson;
  blockId?: number;
  updateLessons?: (lessonId?: number) => Promise<void>;
  coursePath?: CoursePath;
};

export default function LessonResourceContainer(props: Props) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const [loading, setLoading] = useState(false);

  const { lesson, scheduledLesson, blockId, updateLessons, coursePath } = props;

  const { allowBigChanges } = useCourseEditing();

  const { user } = useAuth();

  const isExtraLesson = ROLES.SCHEDULED_LESSON.isExtraLesson(
    scheduledLesson?.type,
  );

  const lessonId = lesson.id;

  const slugCourseName = coursePath?.slug;

  const lessonStatus = lesson.status;

  const isPublishedLesson = isPulished(lessonStatus);

  const listRef = useRef<HTMLUListElement>(null);

  const filters = {
    pageSize,
  };

  const { queryKey, queryFn } = lessonResourceQuerykeys.list(lessonId, filters)
    ._ctx.infinity;

  const {
    results: resources,
    isInitialLoading: loadingResources,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    invalidate,
    cacheManager,
  } = useInfiniteService({
    queryKey,
    queryFn,
  });

  const versionedLesson = async (lessonId: number, slugCourseName?: string) => {
    const newLesson = await updateLesson(
      {
        lessonId,
        slugCourseName: isExtraLesson ? undefined : slugCourseName,
        blockId,
      },
      {
        status: isExtraLesson ? VersioningStatusEnum.VERSIONING : undefined,
      },
    );

    return newLesson;
  };

  const mappedResourceActivities = (
    lessonId: number,
    activitiesIds: number[],
  ) => {
    return activitiesIds.map(async activityId => {
      const versionedActivity = await updateActivityFromLesson({
        activityId,
        lessonId,
      });

      return versionedActivity.id;
    });
  };

  const versionedResource = async (lessonId: number, resourceId: number) => {
    const newResource = await updateLessonResource(lessonId, resourceId);

    if (newResource.activitiesIds.length) {
      await Promise.allSettled(
        mappedResourceActivities(lessonId, newResource.activitiesIds),
      );
    }

    return newResource;
  };

  const updateVersionedItems = async () => {
    await invalidate();
    await updateLessons?.();
  };

  const create = async (body: Partial<ResourceFields>) => {
    const { scheduledLessonId, ...rest } = body;

    let uploadingProgress = 0;
    const tempId = Date.now();

    let auxLesson: PickLesson;

    try {
      setLoading(true);

      if (isPublishedLesson && !isExtraLesson) {
        auxLesson = await versionedLesson(lessonId, slugCourseName);
      } else {
        auxLesson = lesson;
      }

      if (auxLesson) {
        const auxResource = new Resource({
          ...rest,
          id: tempId,
          uploadingProgress,
          uploading: true,
        });

        cacheManager.add(auxResource, { position: 'end' });

        const data = await createLessonResource(
          auxLesson.id,
          rest,
          progressEvent => {
            if (uploadingProgress === 0) {
              listRef.current?.scrollTo({ top: 9999, behavior: 'smooth' });
            }
            uploadingProgress = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total ?? 1),
            );

            cacheManager.update({
              id: auxResource.id,
              uploadingProgress,
              uploading: true,
            });
          },
        );

        if (data) {
          const { id, ...rest } = data;
          cacheManager.update(
            { id: tempId, ...rest, uploading: false },
            { newId: data.id },
          );

          alert.success(t('saved'));
          await updateLessons?.(auxLesson.id);
        }
      }
    } catch (error) {
      alert.error(getErrorMessage(error));
      cacheManager.remove(tempId);
      await updateVersionedItems();
    } finally {
      setLoading(false);
    }
  };

  const update = async (resourceId: number, body: Partial<ResourceFields>) => {
    let uploadingProgress = 0;

    let auxLesson: PickLesson;
    let auxResource: Resource | undefined;

    try {
      setLoading(true);

      auxResource = await getResource(resourceId);

      cacheManager.update({
        id: auxResource.id,
        uploading: true,
        uploadingProgress: 0,
      });

      if (isPublishedLesson) {
        auxLesson = await versionedLesson(lessonId, slugCourseName);
      } else {
        auxLesson = lesson;
      }

      if (auxResource && isPulished(auxResource.status)) {
        const draftResource = await versionedResource(auxLesson.id, resourceId);

        auxResource = {
          ...draftResource,
          uploading: true,
          uploadingProgress: 0,
        };
      }

      if (!auxLesson || !auxResource) return;

      await updateLessonResource(
        auxLesson.id,
        auxResource.id,
        body,
        progressEvent => {
          uploadingProgress = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total ?? 1),
          );

          cacheManager.update({
            uploadingProgress,
            uploading: true,
            id: resourceId,
          });
        },
      );

      await updateVersionedItems();

      alert.success(t('saved'));
    } catch (error) {
      alert.error(getErrorMessage(error));
      await updateVersionedItems();
    } finally {
      setLoading(false);
    }
  };

  const remove = async (resourceId: number) => {
    let auxLesson: PickLesson;
    let auxResource: Resource | undefined;

    try {
      setLoading(true);

      if (isPublishedLesson) {
        auxLesson = await versionedLesson(lessonId, slugCourseName);
      } else {
        auxLesson = lesson;
      }

      auxResource = await getResource(resourceId);

      if (auxResource && isPulished(auxResource.status)) {
        auxResource = await versionedResource(auxLesson.id, resourceId);
      }

      if (!auxLesson || !auxResource) return;

      await removeLessonResource(auxLesson.id, auxResource.id);

      await updateVersionedItems();

      alert.success(t('deleted'));
    } catch (error) {
      alert.error(getErrorMessage(error));
      await updateVersionedItems();
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <section className="flex flex-col w-full gap-3.5">
      <ConditionalRenderer condition={loadingResources}>
        <LoadingIcon className="w-40 self-center text-secondary-content" />
      </ConditionalRenderer>
      <ConditionalRenderer condition={error}>
        <Text text={getErrorMessage(error)} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={resources.length && !loadingResources && !error}
      >
        <InfinityList
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onReachEnd={fetchNextPage}
          className="gap-2 max-h-[20rem] overflow-x-hidden"
          scroll
          ref={listRef}
        >
          {resources.map(resource => (
            <LessonResourceEditor
              key={resource.id}
              resource={resource}
              removeResource={remove}
              updateResource={update}
              editingAllowed={allowBigChanges}
              klassId={scheduledLesson?.klass}
              slugCourseName={slugCourseName}
              lessonId={lessonId}
            />
          ))}
        </InfinityList>
      </ConditionalRenderer>

      <DynamicResourceForm
        loading={loading}
        user={user}
        createResource={create}
        editingAllowed={allowBigChanges}
      />
    </section>
  );
}
